// HTTP client for the server-side queue, replacing the per-device IndexedDB `queue` database.
//
// A mutation is ONE round trip: the response carries both the ordered snapshot and the
// server-rendered `queue-list` turbo stream, so no listener has to fetch anything back to
// redraw. Every mutation ends with exactly one `supercast-queue-changed` dispatch carrying
// `{ detail: { count, head, rendered } }` - the three listeners (queue list, badge,
// fixed-player up-next) read it instead of re-fetching.

const BASE = "/subscriber_v2/queue_items";

// Only ever read by getQueue, which is the no-event fallback path (a listener connecting fresh).
// It MUST be dropped whenever another device changes the queue, or that fallback re-renders a
// badge from a snapshot the server has already superseded - see invalidate().
let cachedItems = null;
let requestToken = 0;

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

// Broadcasts can arrive out of order relative to an in-flight fetch. The token makes the LAST
// COMPLETED fetch win, and since every response is a full snapshot the cache is always internally
// consistent - never a half-applied diff.
async function request(path, { method = "GET", body } = {}) {
  const token = (requestToken += 1);

  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      // Doubles as the web/native discriminator: only web sends it, so only web is served the
      // `streams` half of the payload.
      "X-Queue-Client-Id": window.queueClientId,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Queue request failed: ${method} ${path} → ${response.status}`);
  }

  const payload = await response.json();
  if (token === requestToken) cachedItems = payload.items;

  return payload;
}

// Called by queue_state_controller when ANOTHER device changed the queue: that signal carries the
// new count and head, so nothing needs re-fetching, but our snapshot is now wrong and must not be
// served to the next getQueue() caller.
export function invalidate() {
  // Bumping the token fences any in-flight response out of the cache too - otherwise a request
  // that started before the change resolves afterwards and writes its superseded snapshot back.
  requestToken += 1;
  cachedItems = null;
}

function announce(payload) {
  const items = payload.items;
  // `streams` is present on mutations only. Applying it here is what makes the queue page's
  // rows update without a second request; Turbo no-ops when #queue-list isn't on the page.
  if (payload.streams) window.Turbo.renderStreamMessage(payload.streams);

  window.dispatchEvent(
    new CustomEvent("supercast-queue-changed", {
      detail: {
        count: items.length,
        head: items[0] ?? null,
        // Tells queue_view the rows are already up to date, so it can skip re-rendering them.
        rendered: Boolean(payload.streams),
      },
    })
  );

  return items;
}

// On failure, re-GET the snapshot rather than trusting the pre-mutation local copy - another
// device may have changed things in the interim.
async function mutate(path, options) {
  try {
    return announce(await request(path, options));
  } catch (error) {
    console.error(error);
    window.showToast?.("Couldn't update your queue");
    try {
      announce(await request(`${BASE}.json`));
    } catch (recoveryError) {
      console.error(recoveryError);
    }
    throw error;
  }
}

export async function getQueue() {
  if (cachedItems) return cachedItems;
  return (await request(`${BASE}.json`)).items;
}

export async function addToQueue({ itemId, itemType }) {
  return mutate(BASE, { method: "POST", body: { item_id: itemId, item_type: itemType } });
}

export async function removeFromQueue(id) {
  return mutate(`${BASE}/${id}`, { method: "DELETE" });
}

// Called after playback starts for an item: if that item is sitting at the head of the queue,
// it's now stale (the user is playing it, not waiting for it), so drop it. No-op otherwise.
export async function dequeueIfHead({ itemId, itemType }) {
  const queue = await getQueue();
  const head = queue[0];
  if (!head) return;

  // Web's play-button value is a Stimulus String, native's and add-to-queue's are Number, and the
  // serializer's itemId is a JSON number - normalize to String before comparing.
  if (String(head.itemId) === String(itemId) && head.itemType === itemType) {
    await removeFromQueue(head.id);
  }
}

// Neighbour ids, never indices: with two devices live, an index computed against a snapshot
// another device already invalidated moves the wrong row.
export async function moveItem(id, beforeId, afterId) {
  return mutate(`${BASE}/${id}/move`, {
    method: "PATCH",
    body: { before_id: beforeId ?? null, after_id: afterId ?? null },
  });
}

// Removes every row up to and including `id`.
export async function removeFromQueueUpTo(id) {
  return mutate(`${BASE}/up_to`, { method: "DELETE", body: { id } });
}

export async function clearQueue() {
  return mutate(`${BASE}/destroy_all`, { method: "DELETE" });
}
